import { useLayoutEffect, type RefObject } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';

interface IViewportResizerProps {
  disabled?: boolean;
  mode?: 'none' | 'native';
  container: RefObject<HTMLDivElement>;
}

export function useViewportResizer(props: IViewportResizerProps) {
  const { container, mode = 'none', disabled = false } = props;
  const { isMobile } = useEnvironment();

  // resize container on keyboard open
  useLayoutEffect(() => {
    const viewport = window.visualViewport;

    if (!isMobile || mode !== 'native' || disabled || !viewport) {
      return;
    }

    const animationFrame = { current: undefined as number | undefined };
    const styledContainer = {
      current: undefined as HTMLDivElement | undefined,
    };

    const scheduleSync = () => {
      if (animationFrame.current !== undefined) {
        window.cancelAnimationFrame(animationFrame.current);
      }

      animationFrame.current = window.requestAnimationFrame(() => {
        animationFrame.current = undefined;

        const currentContainer = container.current;
        if (!currentContainer) {
          return;
        }

        styledContainer.current = currentContainer;
        currentContainer.style.height = `${Math.round(viewport.height)}px`;
        currentContainer.style.transform = `translateY(${Math.round(
          viewport.offsetTop,
        )}px)`;
      });
    };

    scheduleSync();
    viewport.addEventListener('resize', scheduleSync);
    viewport.addEventListener('scroll', scheduleSync);

    return () => {
      viewport.removeEventListener('resize', scheduleSync);
      viewport.removeEventListener('scroll', scheduleSync);

      if (animationFrame.current !== undefined) {
        window.cancelAnimationFrame(animationFrame.current);
      }

      if (styledContainer.current) {
        styledContainer.current.style.height = '';
        styledContainer.current.style.transform = '';
      }
    };
  }, [container, isMobile, mode, disabled]);
}
